import {
  AusCompletionRequirement,
  AusRequirement,
  PathStep,
} from 'app/pages/programs/channels/Channels.constants';
import { parsePathStepOrder } from 'app/pages/programs/channels/Channels.utils';

import { StatusEnum } from 'constants/status';

import {
  AUS_LEG,
  AUS_LEG_CHIP_VARIANT,
  AUS_LEG_ERROR_PREFIX,
  AUS_LEG_ID_PREFIX,
  AUS_RETRY_LABEL,
  AUS_STEP_LABEL,
  NEXT_ACTION_COPY,
  NEXT_ACTION_SEVERITY,
  READY_ACTION_COPY,
  STEP_KEY,
  STEP_NODE_STATUS,
  STEP_SHORT_LABEL,
  STEP_SUBTITLE,
  STEP_TITLE,
  WHOLESALE_AUS_LEG_SKIPPED_TEXT,
} from './PathToSubmission.constants';

// Builds a step entry for a given PathStep key from the channel config, or null when
// that step is disabled on the channel. Keyed by PathStep so buildSubmissionSteps can
// stay a straight map/filter over the channel's (sanitized) configured stepOrder.
const STEP_ENTRY_BUILDERS = {
  [PathStep.PRICING]: ({ pricingStepEnabled }) =>
    pricingStepEnabled
      ? {
          key: STEP_KEY.PRICING,
          title: STEP_TITLE[STEP_KEY.PRICING],
          subtitle: STEP_SUBTITLE[STEP_KEY.PRICING],
        }
      : null,

  [PathStep.LOCK]: ({ lockStepEnabled }) =>
    lockStepEnabled
      ? {
          key: STEP_KEY.LOCK,
          title: STEP_TITLE[STEP_KEY.LOCK],
          subtitle: STEP_SUBTITLE[STEP_KEY.LOCK],
        }
      : null,

  [PathStep.AUS]: ({ ausRequirement }) =>
    ausRequirement && ausRequirement !== AusRequirement.OFF
      ? {
          key: STEP_KEY.AUS,
          title: STEP_TITLE[STEP_KEY.AUS],
          subtitle: STEP_SUBTITLE[STEP_KEY.AUS],
          isOptional: ausRequirement === AusRequirement.OPTIONAL,
          badge:
            ausRequirement === AusRequirement.OPTIONAL
              ? { label: AUS_STEP_LABEL[AusRequirement.OPTIONAL], variant: 'GREY' }
              : null,
        }
      : null,

  [PathStep.CONDITIONS]: ({ conditionsStepEnabled }) =>
    conditionsStepEnabled
      ? {
          key: STEP_KEY.DOCUMENTS_CONDITIONS,
          title: STEP_TITLE[STEP_KEY.DOCUMENTS_CONDITIONS],
          subtitle: STEP_SUBTITLE[STEP_KEY.DOCUMENTS_CONDITIONS],
        }
      : null,

  [PathStep.DISCLOSURES]: ({ disclosureStepEnabled }) =>
    disclosureStepEnabled
      ? {
          key: STEP_KEY.DISCLOSURES,
          title: STEP_TITLE[STEP_KEY.DISCLOSURES],
          subtitle: STEP_SUBTITLE[STEP_KEY.DISCLOSURES],
        }
      : null,
};

/**
 * Build the ordered list of steps for the Path to Submission panel based on the active
 * channel configuration. Registration always leads; the remaining steps follow the
 * channel's configured `stepOrder` (sanitized — null/legacy channels fall back to the
 * default Pricing, Lock, AUS, Conditions, Disclosures order), each included only when
 * its own toggle/requirement is enabled.
 *
 * @param {Object|null} channelConfig - The active channel config from the store.
 * @returns {Array<{ key: string, title: string, subtitle: string, badge?: { label: string, variant: string } }>}
 */
export const buildSubmissionSteps = channelConfig => {
  if (!channelConfig) {
    return [];
  }

  const registrationStep = {
    key: STEP_KEY.REGISTRATION,
    title: STEP_TITLE[STEP_KEY.REGISTRATION],
    subtitle: STEP_SUBTITLE[STEP_KEY.REGISTRATION],
  };

  const orderedSteps = parsePathStepOrder(channelConfig.stepOrder)
    .map(pathStep => STEP_ENTRY_BUILDERS[pathStep](channelConfig))
    .filter(Boolean);

  return [registrationStep, ...orderedSteps];
};

// Each resolver mirrors the guard on the fetch that feeds it: a signal is "resolved" when its
// request has SETTLED **or when it was never going to be issued at all**. The loan-scoped fetches
// below are all guarded on a loanId (and the conditions fetch takes a broker/internal branch), and
// the widget itself only requires applicationStatus === REGISTERED — so a REGISTERED deal carrying
// no loanId issues none of them. Waiting on a request nobody sent would hold the skeleton forever.
const STEP_SIGNAL_RESOLVERS = {
  // The widget renders only for registered loans — nothing to await.
  [STEP_KEY.REGISTRATION]: () => true,
  [STEP_KEY.PRICING]: ({ areCommonInfoRequestsSettled }) => areCommonInfoRequestsSettled,
  [STEP_KEY.LOCK]: ({ areCommonInfoRequestsSettled }) => areCommonInfoRequestsSettled,

  // LoanApplicationContent dispatches the AUS summary only when a loanId exists.
  [STEP_KEY.AUS]: ({ isAusSummarySettled, hasLoanId }) => !hasLoanId || isAusSummarySettled,

  // Conditions: the broker branch needs an applicationId, the internal branch a loanId.
  [STEP_KEY.DOCUMENTS_CONDITIONS]: ({
    isConditionsSettled,
    isBrokerOrSubCompanyUser,
    hasApplicationId,
    hasLoanId,
  }) => {
    const isFetchIssued = isBrokerOrSubCompanyUser ? hasApplicationId : hasLoanId;

    return !isFetchIssued || isConditionsSettled;
  },

  // The bundle-history fetch is guarded on a loanId. The send-action resolution gates the step's
  // CTA too: until its bundle-catalog fetch settles the hook reports no send action, which is
  // indistinguishable from "this user cannot send" — rendering "View Disclosures" and, on click,
  // dumping the broker on the post-send delivery tab (DEV-5694).
  [STEP_KEY.DISCLOSURES]: ({
    isDisclosureHistorySettled,
    isDisclosuresSendActionSettled,
    hasLoanId,
  }) => !hasLoanId || (isDisclosureHistorySettled && isDisclosuresSendActionSettled),
};

/**
 * Has every signal backing the channel's visible steps settled?
 *
 * Every status derivation below reads a falsy signal as "not done", so an in-flight fetch is
 * indistinguishable from a genuine negative — an unfetched bundle history is an empty list, i.e.
 * "disclosures not sent". Callers must therefore render a loading state, not a status, until this
 * returns true, or they will confidently mislabel a completed step (DEV-5615).
 *
 * "Resolved" means SETTLED (succeeded or failed) OR NEVER ISSUED — so neither a failed request nor
 * a request the effects skipped (no loanId) can strand the caller on a permanent loading state.
 * Steps the channel omits are never consulted either.
 *
 * @param {Array} steps - from buildSubmissionSteps
 * @param {{
 *   areCommonInfoRequestsSettled: boolean,
 *   isAusSummarySettled: boolean,
 *   isConditionsSettled: boolean,
 *   isDisclosureHistorySettled: boolean,
 *   isBrokerOrSubCompanyUser: boolean,
 *   hasLoanId: boolean,
 *   hasApplicationId: boolean,
 * }} signalState
 * @returns {boolean}
 */
export const areStepSignalsResolved = (steps, signalState) =>
  steps.every(step => (STEP_SIGNAL_RESOLVERS[step.key] ?? (() => true))(signalState));

/**
 * Derive step status from available store signals. Every status is backed by a
 * real signal — a step is only SUCCESS when its signal genuinely confirms it.
 *
 * @param {string} stepKey
 * @param {{
 *   isPricingDone: boolean,
 *   isLockDone: boolean,
 *   isAusDone: boolean,
 *   isAusApproved: boolean,
 *   isAusApproveEligible: boolean,
 *   ausCompletionRequirement: string,
 *   outstandingConditionsCount: number,
 *   isDisclosureSent: boolean,
 * }} signals
 * @returns {string} StatusEnum value
 */
const deriveStepStatus = (
  stepKey,
  {
    isPricingDone,
    isLockDone,
    isAusDone,
    isAusApproved,
    isAusApproveEligible,
    ausCompletionRequirement,
    outstandingConditionsCount,
    isDisclosureSent,
  },
) => {
  switch (stepKey) {
    case STEP_KEY.REGISTRATION:
      // Always done — the widget only renders for registered loans.
      return StatusEnum.SUCCESS;

    case STEP_KEY.PRICING:
      return isPricingDone ? StatusEnum.SUCCESS : StatusEnum.IN_PROGRESS;

    case STEP_KEY.LOCK:
      return isLockDone ? StatusEnum.SUCCESS : StatusEnum.IN_PROGRESS;

    case STEP_KEY.AUS:
      // Each level reads a real AUS signal off the findings-doc extraction:
      // Approve = risk-approved regardless of eligibility (Approve/Eligible OR
      // Approve/Ineligible); Approve/Eligible = the stricter approve+eligible; Run
      // (default) = a completed AUS run exists.
      if (ausCompletionRequirement === AusCompletionRequirement.APPROVE) {
        return isAusApproved ? StatusEnum.SUCCESS : StatusEnum.IN_PROGRESS;
      }

      if (ausCompletionRequirement === AusCompletionRequirement.APPROVE_ELIGIBLE) {
        return isAusApproveEligible ? StatusEnum.SUCCESS : StatusEnum.IN_PROGRESS;
      }

      return isAusDone ? StatusEnum.SUCCESS : StatusEnum.IN_PROGRESS;

    case STEP_KEY.DOCUMENTS_CONDITIONS:
      // Done = no outstanding (unresolved) conditions on the loan.
      return outstandingConditionsCount === 0 ? StatusEnum.SUCCESS : StatusEnum.IN_PROGRESS;

    case STEP_KEY.DISCLOSURES:
      // Done = a disclosure bundle has been delivered (LoanBundleHistory).
      return isDisclosureSent ? StatusEnum.SUCCESS : StatusEnum.IN_PROGRESS;

    default:
      return StatusEnum.IN_PROGRESS;
  }
};

/**
 * Build the SEALED status map for the ordered steps. A step is only DONE when its
 * own signal is satisfied AND every earlier REQUIRED step is also done — so a later
 * step whose raw signal is vacuously true (no conditions on the loan yet, an AUS
 * summary record that predates a real run) can't show DONE while an upstream step is
 * still blocking. An unmet OPTIONAL step does not seal what follows.
 *
 * @param {Array} steps - from buildSubmissionSteps
 * @param {Object} signals
 * @returns {Object<string, string>} step key -> StatusEnum
 */
export const buildStepStatusMap = (steps, signals) =>
  steps.reduce(
    (acc, step) => {
      const isSatisfied = deriveStepStatus(step.key, signals) === StatusEnum.SUCCESS;
      const isDone = acc.reached && isSatisfied;

      return {
        // A still-unmet REQUIRED step seals every later step to not-done; an unmet
        // OPTIONAL step does not block what follows.
        reached: acc.reached && (isSatisfied || !!step.isOptional),
        statusMap: {
          ...acc.statusMap,
          [step.key]: isDone ? StatusEnum.SUCCESS : StatusEnum.IN_PROGRESS,
        },
      };
    },
    { reached: true, statusMap: {} },
  ).statusMap;

/**
 * Count steps that are done (SUCCESS) and steps that are blocking (required + not SUCCESS).
 *
 * @param {Array} steps - from buildSubmissionSteps
 * @param {Object<string, string>} statusMap - from buildStepStatusMap
 * @returns {{ doneCount: number, blockingCount: number }}
 */
export const countStepProgress = (steps, statusMap) =>
  steps.reduce(
    (acc, step) => {
      if (statusMap[step.key] === StatusEnum.SUCCESS) {
        return { ...acc, doneCount: acc.doneCount + 1 };
      }

      if (!step.isOptional) {
        return { ...acc, blockingCount: acc.blockingCount + 1 };
      }

      return acc;
    },
    { doneCount: 0, blockingCount: 0 },
  );

/**
 * Whole-number completion percentage for the progress bar.
 *
 * @param {number} doneCount
 * @param {number} total
 * @returns {number}
 */
const PERCENT_MULTIPLIER = 100;

export const computeDonePercent = (doneCount, total) =>
  total === 0 ? 0 : Math.round((doneCount / total) * PERCENT_MULTIPLIER);

/**
 * The step the broker should act on next = the first step that is not done.
 *
 * @param {Array} steps - from buildSubmissionSteps
 * @param {Object<string, string>} statusMap - from buildStepStatusMap
 * @returns {string|null} the active step key, or null when every step is done
 */
export const deriveActiveStepKey = (steps, statusMap) => {
  const firstUnfinished = steps.find(step => statusMap[step.key] !== StatusEnum.SUCCESS);

  return firstUnfinished ? firstUnfinished.key : null;
};

/**
 * Map a step to its node status for the horizontal rail. Done steps show as DONE;
 * the single active step shows as BLOCKING (required) or CURRENT (optional); every
 * other not-done step shows as TODO (upcoming) so the rail highlights one focus.
 *
 * @param {{ key: string, isOptional?: boolean }} step
 * @param {string|null} activeStepKey
 * @param {string} stepStatus - the SEALED StatusEnum for this step (from buildStepStatusMap)
 * @returns {string} STEP_NODE_STATUS value
 */
export const deriveNodeStatus = (step, activeStepKey, stepStatus) => {
  if (stepStatus === StatusEnum.SUCCESS) {
    return STEP_NODE_STATUS.DONE;
  }

  if (step.key === activeStepKey) {
    return step.isOptional ? STEP_NODE_STATUS.CURRENT : STEP_NODE_STATUS.BLOCKING;
  }

  return STEP_NODE_STATUS.TODO;
};

/**
 * Build the rail model: each step decorated with its short label, index, and node status.
 *
 * @param {Array} steps
 * @param {string|null} activeStepKey
 * @param {Object<string, string>} statusMap - from buildStepStatusMap
 * @returns {Array<{ key: string, label: string, index: number, nodeStatus: string }>}
 */
export const buildRailSteps = (steps, activeStepKey, statusMap) =>
  steps.map((step, index) => ({
    key: step.key,
    label: STEP_SHORT_LABEL[step.key],
    index: index + 1,
    nodeStatus: deriveNodeStatus(step, activeStepKey, statusMap[step.key]),
  }));

/**
 * Build the next-action callout descriptor from the active step.
 *
 * @param {{ key: string, isOptional?: boolean }|null} activeStep
 * @returns {{ severity: string, title: string, description: string }}
 */
export const buildNextAction = activeStep => {
  if (!activeStep) {
    return {
      severity: NEXT_ACTION_SEVERITY.READY,
      title: READY_ACTION_COPY.title,
      description: READY_ACTION_COPY.description,
    };
  }

  const copy = NEXT_ACTION_COPY[activeStep.key] ?? {
    title: 'Next step',
    description: 'Continue toward submission.',
  };

  return {
    severity: activeStep.isOptional
      ? NEXT_ACTION_SEVERITY.IN_PROGRESS
      : NEXT_ACTION_SEVERITY.BLOCKING,
    title: copy.title,
    description: copy.description,
  };
};

/**
 * Find a loan action carrying the given loan-action type.
 *
 * @param {Array<{ loanActionTypes?: Array<{ type: string }> }>} loanActions
 * @param {string} type - A LoanEditorActionsEnum value.
 * @returns {Object|null}
 */
export const findLoanActionByType = (loanActions, type) =>
  loanActions.find(action => (action.loanActionTypes ?? []).some(lt => lt.type === type)) ?? null;

const getLegSuccessText = summary => {
  const recommendation = (summary?.recommendation ?? '').trim();

  if (recommendation) {
    return recommendation;
  }

  return summary?.approveEligibility ? 'Eligible' : null;
};

/**
 * Split the backend's aggregated wholesale-AUS error ("Du: <msg> | Lp: <msg>") into a
 * per-leg map. A leg with no attributed segment stays null; a non-attributable message
 * (no recognized prefix) yields an all-null map so callers fall back to a generic retry.
 *
 * @param {string|null} message
 * @returns {{ DU: string|null, LPA: string|null }}
 */
export const parseAusLegErrors = message => {
  const legErrors = { [AUS_LEG.DU]: null, [AUS_LEG.LP]: null };

  if (!message) {
    return legErrors;
  }

  message.split('|').forEach(segment => {
    const separatorIndex = segment.indexOf(':');

    if (separatorIndex === -1) {
      return;
    }

    const prefix = segment.slice(0, separatorIndex).trim().toLowerCase();
    const text = segment.slice(separatorIndex + 1).trim();

    if (!text) {
      return;
    }

    if (prefix === AUS_LEG_ERROR_PREFIX[AUS_LEG.DU]) {
      legErrors[AUS_LEG.DU] = text;
    }

    if (prefix === AUS_LEG_ERROR_PREFIX[AUS_LEG.LP]) {
      legErrors[AUS_LEG.LP] = text;
    }
  });

  return legErrors;
};

/**
 * Retry-CTA label for the failed leg(s): a single failed leg names itself, both (or an
 * unattributable error) fall back to the generic label.
 *
 * @param {{ DU: string|null, LPA: string|null }|null} legErrors
 * @returns {string}
 */
export const buildAusRetryLabel = legErrors => {
  const duFailed = !!legErrors?.[AUS_LEG.DU];
  const lpFailed = !!legErrors?.[AUS_LEG.LP];

  if (duFailed && !lpFailed) {
    return AUS_RETRY_LABEL[AUS_LEG.DU];
  }

  if (lpFailed && !duFailed) {
    return AUS_RETRY_LABEL[AUS_LEG.LP];
  }

  return AUS_RETRY_LABEL.BOTH;
};

const buildLegProgressText = (leg, id) =>
  id ? `${leg}: ${AUS_LEG_ID_PREFIX[leg]} ${id}` : `${leg}: ${WHOLESALE_AUS_LEG_SKIPPED_TEXT}`;

/**
 * Per-leg description for the in-progress ("Retrieving AUS findings…") callout: a running
 * leg echoes its casefile/key, an absent leg reads as skipped.
 *
 * @param {{ duCasefileId: string|null, lpAusKey: string|null }} casefileIntake
 * @returns {string}
 */
export const buildWholesaleAusInProgressDescription = casefileIntake =>
  [
    buildLegProgressText(AUS_LEG.DU, casefileIntake?.duCasefileId ?? null),
    buildLegProgressText(AUS_LEG.LP, casefileIntake?.lpAusKey ?? null),
  ].join(' · ');

/**
 * Build the DU/LPA status chip for a single wholesale AUS leg from real signals. An attributed
 * retrieval error on the leg's LATEST operation wins first — so a fresh retry-error surfaces its
 * red chip even when a stale prior summary still marks the step Done; success-wins applies only to
 * a leg with no current error. Then a returned finding (success), then an in-flight retrieval,
 * otherwise the provided/skipped state.
 *
 * @param {{
 *   leg: string,
 *   id: string|null,
 *   summary: Object|null,
 *   isSubmitting: boolean,
 *   errorText: string|null,
 * }} args
 * @returns {{ key: string, title: string, variant: string }}
 */
const buildLegChip = ({ leg, id, summary, isSubmitting, errorText }) => {
  const idLabel = id ? `${AUS_LEG_ID_PREFIX[leg]} ${id}` : null;

  if (errorText) {
    return { key: leg, title: `${leg} · ${errorText}`, variant: AUS_LEG_CHIP_VARIANT.ERROR };
  }

  const successText = getLegSuccessText(summary);

  if (successText) {
    return {
      key: leg,
      title: idLabel ? `${leg} · ${successText} · ${idLabel}` : `${leg} · ${successText}`,
      variant: AUS_LEG_CHIP_VARIANT.SUCCESS,
    };
  }

  if (id && isSubmitting) {
    return {
      key: leg,
      title: `${leg} · Retrieving — ${idLabel}`,
      variant: AUS_LEG_CHIP_VARIANT.RETRIEVING,
    };
  }

  if (id) {
    return { key: leg, title: `${leg} · Provided`, variant: AUS_LEG_CHIP_VARIANT.NEUTRAL };
  }

  const emptyText = isSubmitting ? 'Skipped (no key)' : 'Not provided';

  return { key: leg, title: `${leg} · ${emptyText}`, variant: AUS_LEG_CHIP_VARIANT.NEUTRAL };
};

/**
 * Build both wholesale AUS leg chips (Fannie DU, Freddie LPA) for the step row.
 *
 * @param {{
 *   casefileIntake: { duCasefileId: string|null, lpAusKey: string|null },
 *   ausSummary: Object|null,
 *   isSubmitting: boolean,
 *   legErrors: { DU: string|null, LPA: string|null }|null,
 * }} args
 * @returns {Array<{ key: string, title: string, variant: string }>}
 */
export const buildWholesaleAusChips = ({ casefileIntake, ausSummary, isSubmitting, legErrors }) => [
  buildLegChip({
    leg: AUS_LEG.DU,
    id: casefileIntake?.duCasefileId ?? null,
    summary: ausSummary?.duSummary ?? null,
    isSubmitting,
    errorText: legErrors?.[AUS_LEG.DU] ?? null,
  }),
  buildLegChip({
    leg: AUS_LEG.LP,
    id: casefileIntake?.lpAusKey ?? null,
    summary: ausSummary?.lpSummary ?? null,
    isSubmitting,
    errorText: legErrors?.[AUS_LEG.LP] ?? null,
  }),
];
